import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Card, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useCreateExpertMutation,
  useGetExpertByIdQuery,
  useUpdateExpertMutation
} from '../../store/api/expert/expert'
import { ICreateExpert, TCreateExpertInput, TSelectOptionType } from '../../store/api/expert/model'
import CreatableSelect from 'react-select/creatable'
import { MultiValue } from 'react-select'
import { SOCIALS } from '../../utils/constants'
import { ISelectData } from '../../ui/Selects/select/model'
import makeAnimated from 'react-select/animated'
import Loading from '../Loading/Loading'
import { toast } from 'react-toastify'
import { responseError, validationRequaired } from '../../validation/validationRequaired'
import { IValidationRequired } from '../../validation/model'
import MultiSelect from '../../ui/Selects/select/MultiSelect'
import { ISelectDataWithLink } from '../../global.model'

const animatedComponents = makeAnimated()

const ExpertsCreate = (): JSX.Element => {
  const { t: tGeneral } = useTranslation('general')
  const { t: tMessage } = useTranslation('messages')
  const { t: tRequired } = useTranslation('required')
  const { id: expertId } = useParams()
  const navigate = useNavigate()
  const [createExpert] = useCreateExpertMutation()
  const [updateExpert] = useUpdateExpertMutation()
  const {
    isLoading,
    data: expertById,
    refetch
  } = useGetExpertByIdQuery(Number(expertId), { skip: !expertId })

  const [sendToBeck, setSendToBeck] = useState<ICreateExpert>({} as ICreateExpert)
  const [errorRequired, setErrorRequired] = useState<IValidationRequired>({})
  const [socialInputs, setSocialInputs] = useState<ISelectDataWithLink[]>([])

  const onChange = (name: string, value: string | File | MultiValue<TSelectOptionType>): void => {
    setSendToBeck(prev => ({ ...prev, [name]: value }))
  }

  const handleCreateExpert = async (): Promise<void> => {
    const errors = validationRequaired({
      name: sendToBeck.name,
      nameAr: sendToBeck.nameAr,
      longDescription: sendToBeck.longDescription,
      longDescriptionAr: sendToBeck.longDescriptionAr,
      shortDescription: sendToBeck.shortDescription,
      shortDescriptionAr: sendToBeck.shortDescriptionAr
    })

    setErrorRequired(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    const socials = Object.fromEntries(
      socialInputs.filter(({ link }) => Boolean(link)).map(({ label, link }) => [label, link])
    )

    const expertPayload: TCreateExpertInput = {
      ...sendToBeck,
      books: sendToBeck.books?.map(item => item?.value),
      interviews: sendToBeck.interviews?.map(item => item?.value),
      courses: sendToBeck.courses?.map(item => item?.value),
      socials
    }
    const res = expertId ? await updateExpert(expertPayload) : await createExpert(expertPayload)

    if ('error' in res) {
      responseError(res)
    } else {
      toast.success('Expert created successfully!')
      navigate(-1)
    }
  }

  useEffect(() => {
    if (expertById) {
      setSendToBeck(() => ({
        ...expertById,
        books: expertById.books?.map(label => ({ label, value: label })),
        interviews: expertById.interviews?.map(label => ({ label, value: label })),
        courses: expertById.courses?.map(label => ({ label, value: label }))
      }))
      if (expertById?.socials) {
        setSocialInputs(
          Object.entries(expertById.socials).map(([label, link]) => ({
            label,
            value: label,
            link
          }))
        )
      }
    }
  }, [expertById])

  useEffect(() => {
    if (expertId) {
      refetch()
    }
  }, [expertId])

  return (
    <div>
      {expertId && isLoading ? <Loading /> : null}
      <Card.Header>
        <h2>
          {tGeneral(expertId ? 'edit' : 'create')} {tGeneral('expert')}
        </h2>
      </Card.Header>
      <Row>
        <Col lg={12} xl={12} md={12} sm={12}>
          <Card>
            <Card.Body>
              <Row>
                <Col xs={6} md={6}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('name')}
                  </label>
                  <input
                    type='text'
                    defaultValue={sendToBeck?.name}
                    name='name'
                    className='form-control'
                    placeholder={tGeneral('name')}
                    onChange={({ target }) => onChange(target.name, target.value)}
                  />
                  {errorRequired?.name && <span className='text-danger'>{tRequired('name')}</span>}
                </Col>
                <Col xs={6} md={6}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('nameAr')}
                  </label>
                  <input
                    type='text'
                    defaultValue={sendToBeck?.nameAr}
                    name='nameAr'
                    className='form-control'
                    dir='rtl'
                    placeholder={tGeneral('nameAr')}
                    onChange={({ target }) => onChange(target.name, target.value)}
                  />
                  {errorRequired?.nameAr && (
                    <span className='text-danger'>{tRequired('nameAr')}</span>
                  )}
                </Col>
                <Col xs={6} md={6}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('short')} {tGeneral('description')}
                  </label>
                  <input
                    type='text'
                    defaultValue={sendToBeck?.shortDescription}
                    name='shortDescription'
                    className='form-control'
                    placeholder={`${tGeneral('short')} ${tGeneral('description')}`}
                    onChange={({ target }) => onChange(target.name, target.value)}
                  />
                  {errorRequired?.shortDescription && (
                    <span className='text-danger'>{tRequired('shortDescription')}</span>
                  )}
                </Col>
                <Col xs={6} md={6}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('short')} {tGeneral('descriptionAr')}
                  </label>
                  <input
                    type='text'
                    defaultValue={sendToBeck?.shortDescriptionAr}
                    name='shortDescriptionAr'
                    dir='rtl'
                    className='form-control'
                    placeholder={`${tGeneral('short')} ${tGeneral('descriptionAr')}`}
                    onChange={({ target }) => onChange(target.name, target.value)}
                  />
                  {errorRequired?.shortDescriptionAr && (
                    <span className='text-danger'>{tRequired('shortDescriptionAr')}</span>
                  )}
                </Col>
                <Col xs={6} md={6}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('long')} {tGeneral('description')}
                  </label>
                  <input
                    type='text'
                    defaultValue={sendToBeck?.longDescription}
                    name='longDescription'
                    className='form-control'
                    placeholder={`${tGeneral('long')} ${tGeneral('descriptionAr')}`}
                    onChange={({ target }) => onChange(target.name, target.value)}
                  />
                  {errorRequired?.longDescription && (
                    <span className='text-danger'>{tRequired('longDescription')}</span>
                  )}
                </Col>
                <Col xs={6} md={6}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('long')} {tGeneral('descriptionAr')}
                  </label>
                  <input
                    type='text'
                    defaultValue={sendToBeck?.longDescriptionAr}
                    name='longDescriptionAr'
                    className='form-control'
                    dir='rtl'
                    placeholder={`${tGeneral('long')} ${tGeneral('descriptionAr')}`}
                    onChange={({ target }) => onChange(target.name, target.value)}
                  />
                  {errorRequired?.longDescriptionAr && (
                    <span className='text-danger'>{tRequired('longDescriptionAr')}</span>
                  )}
                </Col>
                <Col xs={6} md={7}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('file')}
                  </label>
                  <input
                    defaultValue={sendToBeck?.file}
                    type='file'
                    name='file'
                    className='form-control'
                    accept='image/*'
                    onChange={({ target }) => onChange(target.name, target.files[0])}
                  />
                  {sendToBeck?.image && (
                    <div className='avatar avatar-lg  brround cover-image bg-primary mr-10'>
                      <img
                        style={{ width: '200px' }}
                        src={sendToBeck?.image}
                        className='avatar avatar-lg  brround cover-image bg-primary object-fit-contain'
                        alt=''
                      />
                    </div>
                  )}
                </Col>
                <Col xs={6} md={7}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    Social
                  </label>
                  <MultiSelect
                    data={SOCIALS as unknown as ISelectData[]}
                    editData={socialInputs as unknown as ISelectData[]}
                    setData={setSocialInputs as unknown as Dispatch<SetStateAction<ISelectData[]>>}
                  />
                  {socialInputs.map((item, index) => (
                    <div key={item.value} className='mt-2 d-flex align-items-center gap-2'>
                      <label className='mb-0 text-dark small' style={{ minWidth: 70 }}>
                        {item.label} Link
                      </label>
                      <input
                        type='url'
                        className='form-control form-control-sm w-100'
                        value={item.link}
                        onChange={e => {
                          const value = e.target.value
                          setSocialInputs(prev => {
                            const copy = [...prev]
                            copy[index] = { ...copy[index], link: value }
                            return copy
                          })
                        }}
                      />
                    </div>
                  ))}
                </Col>
                <Col xs={12} md={7}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('course')}
                  </label>
                  <CreatableSelect
                    isMulti
                    value={sendToBeck?.courses}
                    onChange={(value: MultiValue<TSelectOptionType>) => onChange('courses', value)}
                    placeholder={tMessage('typeAndPressEnter')}
                    classNamePrefix='react-select'
                    noOptionsMessage={() => tMessage('typeAndPressEnterToAdd')}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    components={animatedComponents}
                  />
                </Col>

                <Col xs={12} md={7}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('interviews')}
                  </label>
                  <CreatableSelect
                    isMulti
                    value={sendToBeck?.interviews}
                    onChange={(value: MultiValue<TSelectOptionType>) =>
                      onChange('interviews', value)
                    }
                    placeholder={tMessage('typeAndPressEnter')}
                    classNamePrefix='react-select'
                    noOptionsMessage={() => tMessage('typeAndPressEnterToAdd')}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    components={animatedComponents}
                  />
                </Col>

                <Col xs={12} md={7}>
                  <label className='text-dark  mt-5' htmlFor='exampleInputname'>
                    {tGeneral('books')}
                  </label>
                  <CreatableSelect
                    isMulti
                    value={sendToBeck?.books}
                    onChange={(value: MultiValue<TSelectOptionType>) => onChange('books', value)}
                    placeholder={tMessage('typeAndPressEnter')}
                    classNamePrefix='react-select'
                    noOptionsMessage={() => tMessage('typeAndPressEnterToAdd')}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    components={animatedComponents}
                  />
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className='text-end'>
              <button className='btn btn-success mt-1 me-2' onClick={() => handleCreateExpert()}>
                {tGeneral('save')}
              </button>
              <button
                type='button'
                className='btn btn-danger mt-1 me-2'
                onClick={() => navigate(-1)}
              >
                {tGeneral('cancel')}
              </button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default ExpertsCreate
